/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useEffect, useState } from "react";
import { AlertColor, Box, CircularProgress, FormControl, FormHelperText, InputAdornment, Paper } from "@mui/material";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import { IServerImage, useServerImageService } from "../services/registry/ServerImageService";
import { Notify } from "./Notify";

export interface ServerImageBranchPickerProps {
  onBranchSelected?: (branch: string) => void;
  error?: string;
}

export const ServerImageBranchPicker = ({ onBranchSelected, error }: ServerImageBranchPickerProps) => {
  const serverImageService = useServerImageService();

  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [branches, setBranches] = useState<string[]>([]);
  const [filteredBranches, setFilteredBranches] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  const [selectedBranch, setSelectedBranch] = useState<string | undefined>(undefined);
  const [internalError, setInternalError] = useState<string | undefined>(undefined);

  useEffect(() => {
    fetchServerImageBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilteredBranches(branches.filter((branch) => branch.toLowerCase().includes(searchValue.toLowerCase())));
  }, [searchValue, branches]);

  const fetchServerImageBranches = async () => {
    setLoading(true);
    try {
      const res = await serverImageService.listServerImages();
      const serverImages = res.list as IServerImage[];

      // Extract unique branches from the server images
      const uniqueBranches = Array.from(new Set(serverImages.map((img) => img.branch)))
        .filter(Boolean)
        .sort();

      setBranches(uniqueBranches);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to load server image branches.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const handleSelection = (branch: string) => {
    setSelectedBranch(branch);
    setInternalError(undefined);
    onBranchSelected?.(branch);
  };

  // Use external error if provided, otherwise use internal error state
  const displayError = error || internalError;

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <FormControl sx={{ width: "100%" }} error={!!displayError}>
          <TextField
            id="search-input"
            label="Search"
            variant="standard"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            style={{ marginBottom: 12, width: "100%" }}
            error={!!displayError}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Paper elevation={0} style={{ maxHeight: 300, overflow: "auto" }}>
            <List dense disablePadding>
              {filteredBranches.map((branch) => (
                <ListItem key={branch} disableGutters>
                  <ListItemButton selected={selectedBranch === branch} onClick={() => handleSelection(branch)}>
                    <ListItemText primary={branch} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>

          {displayError && <FormHelperText error>{displayError}</FormHelperText>}
        </FormControl>
      )}
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
    </Box>
  );
};
